import React, { useEffect, useRef, useState } from "react";
import moment from "moment";

import Modal from "../../../components/Modal";
import Button from "../../../components/Button";
import Input from "../../../components/Input";
import Table from "../../../components/Table";
import DotStatus from "../../../components/DotStatus";
import UserCell from "../../../components/UserCell";

import api from "../../../services/api";
import { renderError } from "../../../helpers/errors";
import { convertIntToMoney } from "../../../helpers/conversions";

export const orderCustomerName = order => (
    order?.customer_company_trade_name || order?.customer_company_company_name || order?.customer_name || ''
);

const CustomerCell = ({ order }) => {
    const name = orderCustomerName(order);
    const details = [
        order.customer_company_company_name !== name ? order.customer_company_company_name : null,
        order.customer_name !== name ? order.customer_name : null,
    ].filter(Boolean);

    return (
        <div className={`link-order-customer`}>
            <strong>{name || 'Cliente não informado'}</strong>
            {details.length > 0 && <span>{details.join(' · ')}</span>}
        </div>
    );
};

export const ModalLinkOrder = ({ show, close, selectedOrder, onSelect, level }) => {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [selected, setSelected] = useState(null);
    const abortRef = useRef(null);

    useEffect(() => {
        if (!show) return;

        setSearch('');
        setOrders([]);
        setSelected(selectedOrder ?? null);
    }, [show]);

    useEffect(() => {
        if (!show) return;

        const term = search.trim();
        const timer = setTimeout(() => load(term), term ? 300 : 0);

        return () => clearTimeout(timer);
    }, [show, search]);

    const load = term => {
        if (abortRef.current) abortRef.current.abort();
        abortRef.current = new AbortController();

        setLoading(true);

        api.get(`/purchase-order/linkable-orders`, {
            params: { search: term || undefined },
            signal: abortRef.current.signal,
        }).then(res => {
            setOrders(res.data?.resources ?? []);
            setLoading(false);
        }).catch(error => {
            if (error.code === 'ERR_CANCELED') return;
            renderError(error);
            setLoading(false);
        });
    };

    const confirm = () => {
        onSelect(selected);
        close();
    };

    const rows = orders.map(order => {
        const className = selected?.id === order.id ? 'link-order-selected' : '';

        return {
            action: () => setSelected(order),
            className,
            data: [
                <strong>#{order.code}</strong>,
                order.created_at ? moment(order.created_at).format('DD/MM/YYYY') : '—',
                <CustomerCell order={order} />,
                <UserCell name={order.admin_name} photo={order.admin_photo_url} />,
                <span className={`link-order-status`}>
                    <DotStatus color={order.status_color} size={12} />
                    {order.status_name}
                </span>,
                convertIntToMoney(order.total ?? 0, true),
            ],
        };
    });

    return (
        <Modal
            level={level}
            className={`link-order-modal`}
            size={`big`}
            title={`Vincular pedido de venda`}
            show={show}
            close={close}
            footer={
                <>
                    <Button type={`secondary`} size={`small`} text={`Cancelar`} action={close} />
                    <Button
                        type={`primary`}
                        size={`small`}
                        svg={`link`}
                        text={`Vincular`}
                        action={confirm}
                        disabled={!selected}
                    />
                </>
            }
        >
            <div className="section link-order">
                <Input
                    label={`Buscar por número do pedido, razão social, nome fantasia ou contato`}
                    value={search}
                    change={setSearch}
                />

                <Table
                    header={['Pedido', 'Data', 'Cliente', 'Vendedor', 'Status', 'Total']}
                    rows={rows}
                    loading={loading}
                />
            </div>
        </Modal>
    );
};

export default ModalLinkOrder;
